<template>
  <div class="shop-detail-page">
    <nav_list :index="4"></nav_list>

    <div class="detail-wrap">
      <div v-if="loading" class="state-card">
        <i class="el-icon-loading"></i>
        <span>订单详情加载中...</span>
      </div>

      <div v-else-if="error" class="state-card state-card--error">
        <div class="state-main">
          <div class="state-title">订单详情加载失败</div>
          <div class="state-desc">{{ error }}</div>
          <div class="state-actions">
            <el-button type="primary" @click="loadOrderInfo">重新加载</el-button>
            <el-button @click="jump('/bus_list?t=1&s=all')">返回景点订单</el-button>
          </div>
        </div>
      </div>

      <template v-else-if="orderInfo">
        <div class="detail-head">
          <div class="detail-head-main">
            <div class="page-tag">票务订单</div>
            <h1 class="page-title">订单详情</h1>
            <div class="page-subtitle">订单号：{{ orderInfo.sn || orderSn }}</div>
          </div>

          <div class="status-strip">
            <span class="status-strip-label">订单状态</span>
            <span class="status-pill" :class="'status-pill--' + currentStatusTheme">
              <i class="status-pill-dot"></i>
              {{ statusText }}
            </span>
          </div>
        </div>

        <div class="detail-layout">
          <div class="detail-main">
            <section class="detail-card hero-card">
              <div class="hero-summary">
                <div class="hero-cover">
                  <img :src="productImage" alt="">
                </div>

                <div class="hero-content">
                  <div class="hero-eyebrow">票券信息</div>
                  <h2 class="hero-title">{{ productTitle }}</h2>
                  <div class="hero-package">{{ packageTitle }}</div>

                  <div class="hero-facts">
                    <div class="fact-item" v-if="shouldShowServiceDate()">
                      <span>出行日期</span>
                      <strong>{{ serviceDateText }}</strong>
                    </div>
                    <div class="fact-item">
                      <span>数量</span>
                      <strong>{{ orderQuantity }} {{ orderUnit || '份' }}</strong>
                    </div>
                    <div class="fact-item" v-if="orderInfo.event_time">
                      <span>出行时段</span>
                      <strong>{{ orderInfo.event_time }}</strong>
                    </div>
                    <div class="fact-item">
                      <span>下单时间</span>
                      <strong>{{ formatDateTime(orderInfo.created_at) || '-' }}</strong>
                    </div>
                    <div class="fact-item" v-if="orderInfo.pay_time">
                      <span>支付时间</span>
                      <strong>{{ formatDateTime(orderInfo.pay_time) }}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="skuLines.length" class="sku-block">
                <div class="section-title">票种明细</div>
                <div class="sku-list">
                  <div v-for="(sku, index) in skuLines" :key="'sku-' + index" class="sku-item">
                    <span class="sku-name">{{ sku.name }}</span>
                    <span class="sku-meta">x{{ sku.qty }}</span>
                    <strong class="sku-price">¥{{ formatMoney(sku.price) }}</strong>
                  </div>
                </div>
              </div>
            </section>

            <section class="detail-card" v-if="voucherList.length">
              <div class="section-title">凭证信息</div>
              <div class="voucher-list">
                <div v-for="(voucher, index) in voucherList" :key="'voucher-' + index" class="voucher-item">
                  <div class="voucher-copy">
                    <div class="voucher-name">凭证 {{ index + 1 }}</div>
                    <div class="voucher-url">{{ getVoucherFileName(voucher) }}</div>
                  </div>
                  <el-button type="text" @click="openVoucher(voucher)">查看凭证</el-button>
                </div>
              </div>
            </section>

            <section class="detail-card" v-if="buyerInfoLines.length">
              <div class="section-title">买家信息</div>
              <div class="info-grid">
                <div v-for="item in buyerInfoLines" :key="item.label" class="info-item">
                  <span class="info-label">{{ item.label }}</span>
                  <strong class="info-value">{{ item.value }}</strong>
                </div>
              </div>
            </section>

            <section class="detail-card" v-if="travelers.length">
              <div class="section-title">出行人信息</div>
              <div class="traveler-list">
                <div v-for="(traveler, index) in travelers" :key="'traveler-' + index" class="traveler-card">
                  <div class="traveler-title">{{ getTravelerTitle(traveler, index) }}</div>
                  <div class="traveler-lines">
                    <div
                      v-for="line in getTravelerDisplayLines(traveler)"
                      :key="line.label + '-' + line.value"
                      class="traveler-line"
                    >
                      <span>{{ line.label }}</span>
                      <strong>{{ line.value }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="detail-card" v-if="refundPolicyLines.length">
              <div class="section-title">{{ refundPolicyTitle || '退款政策' }}</div>
              <ul class="policy-list">
                <li v-for="(item, index) in refundPolicyLines" :key="'refund-' + index">
                  {{ item }}
                </li>
              </ul>
            </section>

            <section class="detail-card" v-if="orderNote">
              <div class="section-title">订单备注</div>
              <div class="note-box">{{ orderNote }}</div>
            </section>
          </div>

          <aside class="detail-side">
            <div class="side-card">
              <div class="side-head">
                <span>订单金额</span>
                <strong>¥ {{ orderAmountText }}</strong>
              </div>

              <div class="summary-list">
                <div class="summary-row">
                  <span>订单编号</span>
                  <strong>{{ orderInfo.sn || '-' }}</strong>
                </div>
                <div class="summary-row" v-if="orderInfo.created_at">
                  <span>下单时间</span>
                  <strong>{{ formatDateTime(orderInfo.created_at) }}</strong>
                </div>
                <div class="summary-row" v-if="orderInfo.pay_time">
                  <span>支付时间</span>
                  <strong>{{ formatDateTime(orderInfo.pay_time) }}</strong>
                </div>
              </div>

              <div class="side-actions">
                <el-button
                  v-if="canPay"
                  type="primary"
                  class="action-btn"
                  @click="jump('/shop/pay', { sn: orderInfo.sn })"
                >
                  立即支付
                </el-button>

                <el-button
                  v-if="canCancelOrder"
                  class="action-btn"
                  @click="cancelOrder"
                >
                  取消订单
                </el-button>

                <el-button
                  v-if="canApplyRefund"
                  class="action-btn"
                  @click="openRefundDialog"
                >
                  申请退款
                </el-button>

                <el-button class="action-btn ghost-btn" @click="jump('/bus_list?t=1&s=all')">
                  返回订单中心
                </el-button>
              </div>
            </div>
          </aside>
        </div>
      </template>
    </div>

    <el-dialog
      title="申请退款"
      :visible.sync="refundDialogVisible"
      width="520px"
    >
      <div class="refund-form">
        <div class="info-row">
          <span class="info-label">订单号</span>
          <span class="info-value">{{ orderInfo ? orderInfo.sn : '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">订单总额</span>
          <span class="info-value">¥{{ refundInfo.total_price || '0.00' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">退款金额</span>
          <span class="info-value refund-info-highlight">¥{{ refundInfo.refund_amount || '0.00' }}</span>
        </div>
        <div v-if="refundInfo.refund_rule" class="refund-rule-card">
          <div class="refund-rule-title">退款说明</div>
          <div class="refund-rule">
            {{ refundInfo.refund_rule }}
          </div>
        </div>
        <div class="refund-field">
          <div class="refund-label">取消原因</div>
          <el-select v-model="refundReason" placeholder="请选择取消原因" style="width: 100%;">
            <el-option
              v-for="reason in cancelReasons"
              :key="reason.value"
              :label="reason.label"
              :value="reason.value"
            />
          </el-select>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="closeRefundDialog">取消</el-button>
        <el-button type="primary" :loading="refundSubmitting" @click="submitRefund">提交申请</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import nav_list from '../index/nav_list.vue';

export default {
  name: 'shop-detail',
  components: {
    nav_list,
  },
  data() {
    return {
      loading: false,
      error: '',
      orderSn: '',
      orderInfo: null,
      orderExtra: null,
      refundDialogVisible: false,
      refundSubmitting: false,
      refundReason: '',
      refundInfo: {
        total_price: '0.00',
        refund_amount: '0.00',
        refund_rule: '',
      },
      cancelReasons: [
        { value: 'MC001', label: '行程变更或取消' },
        { value: 'MC002', label: '重复订购' },
        { value: 'MC003', label: '价格因素' },
        { value: 'MC004', label: '个人因素' },
        { value: 'MC005', label: '交通因素' },
        { value: 'MC006', label: '联系不上店家' },
        { value: 'MC999', label: '其他' },
      ],
    };
  },
  computed: {
    buyerInfo() {
      return this.orderExtra || null;
    },
    travelers() {
      return this.orderExtra && this.orderExtra.custom ? this.orderExtra.custom : [];
    },
    productTitle() {
      return this.orderInfo && this.orderInfo.product && this.orderInfo.product.prod_name
        ? this.orderInfo.product.prod_name
        : '票券订单';
    },
    packageTitle() {
      if (this.orderInfo && this.orderInfo.package && this.orderInfo.package.pkg_name) {
        return this.orderInfo.package.pkg_name;
      }
      if (this.orderExtra && this.orderExtra.pkg_name) {
        return this.orderExtra.pkg_name;
      }
      return '套餐信息';
    },
    productImage() {
      if (this.orderInfo && this.orderInfo.product && this.orderInfo.product.img_list && this.orderInfo.product.img_list.length) {
        return this.orderInfo.product.img_list[0];
      }
      return require('../../../static/image/cover1.png');
    },
    serviceDateText() {
      if (!this.orderInfo) return '-';
      const start = this.formatDisplayDate(this.orderInfo.s_date);
      if (this.orderInfo.e_date && this.orderInfo.e_date !== this.orderInfo.s_date) {
        return start + ' 至 ' + this.formatDisplayDate(this.orderInfo.e_date);
      }
      return start;
    },
    orderQuantity() {
      if (this.orderInfo && this.orderInfo.num) return this.orderInfo.num;
      const skus = this.orderInfo && this.orderInfo.skus ? this.orderInfo.skus : [];
      if (!skus.length) return 1;
      return skus.reduce((sum, item) => sum + Number(item.qty || 0), 0);
    },
    orderUnit() {
      return this.orderExtra && this.orderExtra.unit ? this.orderExtra.unit : '';
    },
    orderAmountText() {
      const value = this.orderInfo && this.orderInfo.b2c_total_price ? this.orderInfo.b2c_total_price : 0;
      return this.formatMoney(value);
    },
    statusText() {
      const map = {
        pending: '待付款',
        paid: '已付款',
        send: '已出券',
        cancelled: '已取消',
        refunded: '已退款',
      };
      if (this.orderInfo && this.orderInfo.pay_status === 'refunding') {
        return '退款中';
      }
      return map[this.orderInfo && this.orderInfo.order_status] || (this.orderInfo && this.orderInfo.order_status) || '未知状态';
    },
    currentStatusTheme() {
      if (!this.orderInfo || !this.orderInfo.order_status) return 'default';
      if (this.orderInfo.order_status === 'send' || this.orderInfo.order_status === 'paid') return 'success';
      if (this.orderInfo.order_status === 'pending') return 'warning';
      if (this.orderInfo.order_status === 'cancelled' || this.orderInfo.order_status === 'refunded') return 'muted';
      return 'default';
    },
    skuLines() {
      const skus = this.orderInfo && this.orderInfo.skus ? this.orderInfo.skus : [];
      return skus.map((sku) => ({
        name: this.getSkuName(sku),
        qty: sku.qty || 0,
        price: sku.price || 0,
      }));
    },
    voucherList() {
      return this.orderInfo && this.orderInfo.vouchers ? this.orderInfo.vouchers : [];
    },
    buyerInfoLines() {
      if (!this.buyerInfo) return [];
      const lines = [];
      const buyerName = [this.buyerInfo.buyer_last_name, this.buyerInfo.buyer_first_name].filter(Boolean).join('');
      if (buyerName) lines.push({ label: '姓名', value: buyerName });
      if (this.buyerInfo.buyer_Email) lines.push({ label: '邮箱', value: this.buyerInfo.buyer_Email });
      if (this.buyerInfo.buyer_tel_number) lines.push({ label: '联系电话', value: this.buyerInfo.buyer_tel_number });
      return lines;
    },
    refundPolicyTitle() {
      const refundPolicy = this.getOrderRefundPolicy();
      return refundPolicy && refundPolicy.module_title ? refundPolicy.module_title : '';
    },
    refundPolicyLines() {
      const props = this.getPmdlProperties(this.getOrderRefundPolicy());
      if (props && props.partial_refund && props.partial_refund.list && props.partial_refund.list.length) {
        return props.partial_refund.list.map((item) => item.desc || '').filter(Boolean);
      }
      if (props && props.policy_type && props.policy_type.desc) {
        return [props.policy_type.desc];
      }
      const detail = this.getOrderRefundDetail();
      if (detail && detail.partial_refund && detail.partial_refund.list && detail.partial_refund.list.length) {
        return detail.partial_refund.list.map((item) => item.desc || '').filter(Boolean);
      }
      return [];
    },
    orderNote() {
      return this.orderExtra && this.orderExtra.order_note ? this.orderExtra.order_note : '';
    },
    canPay() {
      return !!(this.orderInfo && this.orderInfo.order_status === 'pending');
    },
    canCancelOrder() {
      if (!this.orderInfo || this.orderInfo.order_status !== 'pending') return false;
      if (this.orderInfo.sts && typeof this.orderInfo.sts.can_cancel !== 'undefined') {
        return !!this.orderInfo.sts.can_cancel;
      }
      return true;
    },
    canApplyRefund() {
      return !!(this.orderInfo && this.orderInfo.sts && this.orderInfo.sts.can_refunded);
    },
  },
  mounted() {
    this.orderSn = this.$route.query.sn || this.$route.query.orderNo || '';
    if (!this.orderSn) {
      this.error = '缺少订单号';
      return;
    }
    this.loadOrderInfo();
  },
  methods: {
    async loadOrderInfo() {
      if (!this.orderSn) return;
      this.loading = true;
      this.error = '';
      try {
        const response = await this.get('/kkday/orders/' + this.orderSn, {}, 'get', false);
        if (!response || !response.base) {
          this.error = '订单信息获取失败';
          return;
        }
        this.orderInfo = response.base;
        this.orderExtra = response.extra || {};
        if (this.$route.query.action === 'refund' && this.canApplyRefund) {
          this.openRefundDialog();
        }
      } catch (error) {
        this.error = '网络错误，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2);
    },
    formatDateTime(value) {
      if (!value) return '';
      return String(value).replace('T', ' ').slice(0, 16);
    },
    formatDisplayDate(value) {
      if (!value) return '-';
      return String(value).slice(0, 10);
    },
    normalizeRefundDetail(detail) {
      if (!detail) return null;
      if (typeof detail === 'string') {
        try {
          return JSON.parse(detail);
        } catch (error) {
          return null;
        }
      }
      return detail;
    },
    getOrderRefundDetail() {
      if (!this.orderInfo) return null;
      return this.normalizeRefundDetail(this.orderInfo.package_snapshot)
        || this.normalizeRefundDetail(this.orderInfo.package)
        || null;
    },
    getOrderRefundPolicy() {
      const detail = this.getOrderRefundDetail();
      return detail && detail.description_module && detail.description_module.PMDL_REFUND_POLICY
        ? detail.description_module.PMDL_REFUND_POLICY
        : this.orderInfo && this.orderInfo.extra && this.orderInfo.extra.pkg_refund_policy
          ? this.orderInfo.extra.pkg_refund_policy
          : this.orderExtra && this.orderExtra.pkg_refund_policy
            ? this.orderExtra.pkg_refund_policy
            : null;
    },
    getPmdlProperties(moduleData) {
      if (!moduleData) return null;
      if (moduleData.content && moduleData.content.properties) return moduleData.content.properties;
      if (moduleData.properties) return moduleData.properties;
      return moduleData;
    },
    shouldShowServiceDate() {
      if (!this.orderInfo) return false;
      if (this.orderInfo.sts && this.orderInfo.sts.go_date_setting && this.orderInfo.sts.go_date_setting.type === '04') {
        return false;
      }
      if (this.orderInfo.extra && this.orderInfo.extra.extra && this.orderInfo.extra.extra.go_date_setting
        && this.orderInfo.extra.extra.go_date_setting.type === '04') {
        return false;
      }
      if (this.orderInfo.extra && this.orderInfo.extra.go_date_setting && this.orderInfo.extra.go_date_setting.type === '04') {
        return false;
      }
      if (this.orderExtra && this.orderExtra.go_date_setting && this.orderExtra.go_date_setting.type === '04') {
        return false;
      }
      if (!this.orderInfo.s_date) return false;
      return ['0000-00-00', '0000-00-00 00:00:00', ''].indexOf(this.orderInfo.s_date) === -1;
    },
    getSkuName(sku) {
      if (sku.sku_name) return sku.sku_name;
      if (sku.specs_ref && sku.specs_ref.length > 0) {
        const specNames = sku.specs_ref.map((spec) => spec.spec_value).filter(Boolean);
        if (specNames.length > 0) return specNames.join(' ');
      }
      return '票券';
    },
    getTravelerTitle(traveler, index) {
      if (traveler.cus_type === 'contact') return '联系人';
      if (traveler.cus_type === 'send') return '寄送信息';
      if (traveler.cus_type === 'cus_01') return '旅客代表';
      return '旅客 ' + (index + 1);
    },
    getTravelerDisplayLines(traveler) {
      const lines = [];
      const pushLine = (label, value) => {
        if (!value) return;
        lines.push({ label, value });
      };
      pushLine('本地姓名', [traveler.native_last_name, traveler.native_first_name].filter(Boolean).join(''));
      pushLine('英文姓名', [traveler.english_first_name, traveler.english_last_name].filter(Boolean).join(' '));
      pushLine('护照号码', traveler.passport_no);
      pushLine('出生日期', traveler.birth);
      pushLine('联系电话', traveler.tel_number);
      pushLine('国籍', traveler.nationality);
      pushLine('地址', traveler.address);
      pushLine('酒店名称', traveler.hotel_name);
      return lines;
    },
    getVoucherFileName(voucherPath) {
      if (!voucherPath) return '';
      const parts = String(voucherPath).split('/');
      return parts[parts.length - 1] || String(voucherPath);
    },
    openVoucher(voucherPath) {
      if (!voucherPath) return;
      const fullUrl = /^https?:\/\//.test(voucherPath)
        ? voucherPath
        : ('https://api.japanbus.cn/' + String(voucherPath).replace(/^\/+/, ''));
      window.open(fullUrl, '_blank');
    },
    async cancelOrder() {
      if (!this.orderInfo) return;
      try {
        await this.$confirm('确定要取消这个订单吗？', '取消订单', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        });
        const response = await this.get('/kkday/orders/cancel', {
          order_id: this.orderInfo.id,
        }, 'post', false);
        if (response && response.success) {
          this.$message.success('订单已取消');
          await this.loadOrderInfo();
          return;
        }
        this.$message.error((response && response.message) || '取消失败');
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('取消失败，请稍后重试');
        }
      }
    },
    closeRefundDialog() {
      this.refundDialogVisible = false;
      this.refundReason = '';
      this.refundInfo = {
        total_price: '0.00',
        refund_amount: '0.00',
        refund_rule: '',
      };
    },
    async openRefundDialog() {
      if (!this.orderInfo || !this.orderInfo.sn) return;
      this.refundSubmitting = true;
      try {
        const response = await this.get('/kkday/orders/refund/calculate', {
          sn: this.orderInfo.sn,
        }, 'get', false);
        if (!response || !response.can_refund) {
          this.$alert((response && response.refund_rule) || '该订单不支持退款', '无法退款', {
            confirmButtonText: '确定',
          });
          return;
        }
        this.refundInfo = {
          total_price: response.total_price || '0.00',
          refund_amount: response.refund_amount || '0.00',
          refund_rule: response.refund_rule || '',
        };
        this.refundReason = '';
        this.refundDialogVisible = true;
      } catch (error) {
        this.$message.error('获取退款信息失败');
      } finally {
        this.refundSubmitting = false;
      }
    },
    async submitRefund() {
      if (!this.refundReason) {
        this.$message.warning('请选择取消原因');
        return;
      }
      this.refundSubmitting = true;
      try {
        const reasonItem = this.cancelReasons.find((item) => item.value === this.refundReason);
        const response = await this.get('/kkday/orders/refund', {
          sn: this.orderInfo.sn,
          reason: reasonItem ? reasonItem.label : '',
          cancel_type: this.refundReason,
        }, 'post', false);
        if (response && response.refund_status === 'pending') {
          this.$message.success('退款申请已提交');
          this.closeRefundDialog();
          await this.loadOrderInfo();
          return;
        }
        this.$message.error((response && response.message) || '提交失败');
      } catch (error) {
        this.$message.error('提交失败，请稍后重试');
      } finally {
        this.refundSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.shop-detail-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 30%),
    linear-gradient(180deg, #f5f7fb 0%, #eef3f8 100%);
  padding-bottom: 56px;
}

.detail-wrap {
  width: 1200px;
  margin: 24px auto 0;
}

.state-card,
.detail-card,
.side-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.06);
}

.state-card {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  font-size: 16px;
}

.state-main {
  text-align: center;
}

.state-title {
  color: #0f172a;
  font-size: 24px;
  font-weight: 700;
}

.state-desc {
  margin-top: 12px;
  color: #64748b;
}

.state-actions {
  margin-top: 20px;
}

.detail-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.page-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.page-title {
  margin: 14px 0 10px;
  color: #0f172a;
  font-size: 34px;
  line-height: 1.15;
}

.page-subtitle {
  color: #64748b;
  font-size: 14px;
}

.status-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
}

.status-strip-label {
  color: #64748b;
  font-size: 13px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
}

.status-pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.status-pill--success {
  color: #15803d;
  background: #ecfdf5;
}

.status-pill--warning {
  color: #c2410c;
  background: #fff7ed;
}

.status-pill--muted {
  color: #475569;
  background: #f1f5f9;
}

.status-pill--default {
  color: #2563eb;
  background: #eff6ff;
}

.detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
}

.detail-main {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-card {
  padding: 24px 28px;
}

.hero-summary {
  display: flex;
  gap: 22px;
  align-items: flex-start;
}

.hero-cover {
  width: 280px;
  height: 188px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 14px;
  background: #e2e8f0;
}

.hero-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-content {
  min-width: 0;
  flex: 1;
}

.hero-eyebrow {
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.hero-title {
  margin: 10px 0 8px;
  color: #0f172a;
  font-size: 30px;
  line-height: 1.25;
}

.hero-package {
  color: #64748b;
  font-size: 15px;
  line-height: 1.6;
}

.hero-facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid #eef2f7;
}

.fact-item span {
  display: block;
  color: #94a3b8;
  font-size: 12px;
}

.fact-item strong {
  display: block;
  margin-top: 6px;
  color: #1e293b;
  font-size: 15px;
  line-height: 1.6;
}

.sku-block {
  margin-top: 24px;
  padding-top: 22px;
  border-top: 1px solid #eef2f7;
}

.section-title {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 18px;
}

.sku-list,
.voucher-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sku-item,
.voucher-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 80px 120px;
  gap: 14px;
  align-items: center;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
}

.sku-name,
.voucher-name {
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;
}

.sku-meta,
.voucher-url {
  color: #64748b;
  font-size: 13px;
}

.sku-price {
  color: #dc2626;
  font-size: 15px;
  text-align: right;
}

.voucher-item {
  grid-template-columns: minmax(0, 1fr) 90px;
}

.voucher-copy {
  min-width: 0;
}

.voucher-url {
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.info-item {
  padding: 16px 18px;
  border-radius: 12px;
  background: #f8fafc;
}

.info-label {
  display: block;
  color: #94a3b8;
  font-size: 12px;
}

.info-value {
  display: block;
  margin-top: 6px;
  color: #0f172a;
  font-size: 15px;
  line-height: 1.6;
  word-break: break-all;
}

.traveler-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.traveler-card {
  padding: 18px;
  border-radius: 14px;
  background: #f8fafc;
}

.traveler-title {
  color: #111827;
  font-size: 15px;
  font-weight: 700;
}

.traveler-lines {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
}

.traveler-line {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.traveler-line span {
  color: #64748b;
  font-size: 13px;
  flex-shrink: 0;
}

.traveler-line strong {
  color: #0f172a;
  font-size: 13px;
  line-height: 1.6;
  text-align: right;
  word-break: break-word;
}

.policy-list {
  margin: 0;
  padding-left: 20px;
  color: #475569;
  line-height: 1.9;
}

.note-box {
  padding: 16px 18px;
  border-radius: 12px;
  background: #f8fafc;
  color: #334155;
  line-height: 1.9;
}

.detail-side {
  position: relative;
}

.side-card {
  position: sticky;
  top: 24px;
  padding: 24px;
}

.side-head {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 18px;
  border-bottom: 1px solid #eef2f7;
}

.side-head span {
  color: #64748b;
  font-size: 13px;
}

.side-head strong {
  color: #dc2626;
  font-size: 32px;
  line-height: 1;
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 0;
}

.summary-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.summary-row span {
  color: #94a3b8;
  font-size: 12px;
}

.summary-row strong {
  color: #1f2937;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-all;
}

.side-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-btn {
  width: 100%;
}

.ghost-btn {
  margin-left: 0;
}

.refund-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.refund-rule-card {
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
}

.refund-rule-title {
  margin-bottom: 8px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.refund-rule {
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
}

.refund-label {
  margin-bottom: 10px;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.refund-info-highlight {
  color: #dc2626;
  font-weight: 700;
}

@media (max-width: 1280px) {
  .detail-wrap {
    width: calc(100% - 48px);
  }
}

@media (max-width: 1080px) {
  .detail-layout {
    grid-template-columns: 1fr;
  }

  .side-card {
    position: static;
  }

  .hero-summary {
    flex-direction: column;
  }

  .hero-cover {
    width: 100%;
    height: 280px;
  }

  .traveler-list,
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/detail.vue